import { useTranslation } from 'react-i18next'
import { Library, Mic } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SetupFieldsProps } from '@/modules/types'

/**
 * The Podcast or Vídeos space's question: one show (the space opens on it) or several (a list).
 * "Several" comes first and is the default, as in Formulários.
 */
export const ShowsSpaceFields = ({ config, onChange, errors, disabled = false }: SetupFieldsProps) => {
  const { t } = useTranslation('media')
  const mode = config?.mode === 'single' ? 'single' : 'multi'
  const options = [
    { value: 'multi' as const, icon: Library, title: t('spaceMode.multi'), description: t('spaceMode.multiHint') },
    { value: 'single' as const, icon: Mic, title: t('spaceMode.single'), description: t('spaceMode.singleHint') }
  ]
  return (
    <div className="space-y-2" data-testid="shows-mode">
      <span className="text-sm font-medium text-foreground-secondary">{t('spaceMode.label')}</span>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map(({ value, icon: Icon, title, description }) => {
          const selected = mode === value
          return (
            <button key={value} type="button" aria-pressed={selected} data-value={value} disabled={disabled}
              onClick={() => onChange({ ...config, mode: value })}
              className={cn(
                'flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
                'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1',
                selected ? 'border-primary bg-primary-50' : 'border-border bg-surface hover:border-border',
                disabled && 'cursor-not-allowed opacity-50'
              )}>
              <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', selected ? 'text-primary' : 'text-muted-foreground')} />
              <span className="min-w-0">
                <span className={cn('block text-sm font-medium', selected ? 'text-primary-700' : 'text-foreground')}>{title}</span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{description}</span>
              </span>
            </button>
          )
        })}
      </div>
      {mode === 'single' && <p className="text-xs text-muted-foreground">{t('spaceMode.singleNote')}</p>}
      {errors?.mode && <p className="text-xs text-red-600">{errors.mode}</p>}
    </div>
  )
}
